/* ── The Caviar Bar — Theme JS ──────────────────────────────────────────────── */
'use strict';

// ── Helpers ──────────────────────────────────────────────────────────────────

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

function money(cents) {
  const amount = cents / 100;
  const digits = amount % 1 === 0 ? 0 : 2;
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount);
}

function toast(msg) {
  const el = $('#cb-toast');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('show');
  clearTimeout(toast._t);
  toast._t = setTimeout(() => el.classList.remove('show'), 2400);
}

// ── Shopify Cart API ──────────────────────────────────────────────────────────

async function cartAdd(variantId, quantity = 1) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity }),
  });
  if (!res.ok) throw new Error('Cart add failed');
  return res.json();
}

async function cartChange(key, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: key, quantity }),
  });
  if (!res.ok) throw new Error('Cart change failed');
  return res.json();
}

async function cartGet() {
  const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
  return res.json();
}

// ── Cart count updates ────────────────────────────────────────────────────────

function updateCartCount(count) {
  $$('[data-cart-count]').forEach(el => { el.textContent = count; });
  const bagCount = $('#bag-panel-count');
  if (bagCount) bagCount.textContent = count;
  const drawerCount = $('#drawer-count');
  if (drawerCount) drawerCount.textContent = count;
}

const PRODUCT_RATINGS = {
  'royal-beluga-caviar':          { pr: '4.9', rc: '97'  },
  'oscietra-caviar':              { pr: '4.8', rc: '184' },
  'royal-kaluga-caviar':          { pr: '4.7', rc: '156' },
  'royal-baerii-caviar':          { pr: '4.8', rc: '203' },
  'imperial-caviar':              { pr: '4.7', rc: '112' },
  'mother-of-pearl-caviar-spoon': { pr: '4.9', rc: '88'  },
  'caviar-blinis':                { pr: '4.6', rc: '44'  },
  'wild-premium-king-salmon-roe': { pr: '4.8', rc: '29'  },
  'wild-premium-keta-salmon-roe': { pr: '4.7', rc: '38'  },
  'taster-duo-gift-set':          { pr: '4.9', rc: '214' },
  'classic-duo-gift-set':         { pr: '4.8', rc: '178' },
  'premium-duo-gift-set':         { pr: '4.9', rc: '143' },
  'kaluga-oscietra-gift-set':     { pr: '4.8', rc: '97'  },
  'beluga-experience-gift-set':   { pr: '4.9', rc: '86'  },
  'luxury-trio-gift-set':         { pr: '4.8', rc: '119' },
  'the-baruk-collection':         { pr: '4.9', rc: '74'  },
  'beluga-prestige-gift-set':     { pr: '5.0', rc: '52'  },
  'caviar-serving-plate':         { pr: '4.8', rc: '61'  },
  'caviar-tin-opener':            { pr: '4.7', rc: '33'  },
};
const CAVIAR_HANDLES = new Set(['royal-beluga-caviar','oscietra-caviar','royal-kaluga-caviar','royal-baerii-caviar','imperial-caviar']);
const VAT_HANDLES = new Set(['mother-of-pearl-caviar-spoon','caviar-tin-opener','caviar-serving-plate']);
const FREE_DEL_THRESHOLD = 12000;

function updateDeliveryMsg(totalPence) {
  const el = $('#drawer-delivery-msg');
  if (!el) return;
  if (totalPence >= FREE_DEL_THRESHOLD) {
    el.innerHTML = '<span class="free-del-msg">✓ Free delivery</span>';
  } else if (totalPence === 0) {
    el.innerHTML = '<span class="del-calc">Calculated at checkout</span>';
  } else {
    el.innerHTML = `<span class="del-upsell">Add ${money(FREE_DEL_THRESHOLD - totalPence)} more for free delivery</span>`;
  }
}

async function refreshCartUI() {
  const cart = await cartGet();
  updateCartCount(cart.item_count);

  const drawerSubtotal = $('#drawer-subtotal');
  if (drawerSubtotal) drawerSubtotal.textContent = money(cart.total_price);
  const bagSubtotal = $('#bag-subtotal');
  if (bagSubtotal) bagSubtotal.textContent = money(cart.total_price);
  const bagTotal = $('#bag-total');
  if (bagTotal) bagTotal.textContent = money(cart.total_price);

  updateDeliveryMsg(cart.total_price);

  const drawerItems = $('#cart-drawer-items');
  if (drawerItems) renderDrawerItems(cart, drawerItems);

  const miniBag = $('#mini-bag');
  if (miniBag) renderDrawerItems(cart, miniBag);

  return cart;
}

function renderDrawerItems(cart, container) {
  const extrasEl = document.getElementById('drawer-extras');

  if (cart.item_count === 0) {
    container.innerHTML = `
      <div class="empty-bag">
        <p class="empty-bag__headline">Your bag is empty</p>
        <p class="empty-bag__sub">Explore our caviar range or build a gift set.</p>
        <div style="display:flex;gap:10px;flex-wrap:wrap">
          <a href="/collections/all-caviar" class="button primary" style="flex:1;min-width:120px">Shop Caviar</a>
          <a href="/collections/gift-sets" class="button secondary" style="flex:1;min-width:120px">Gift Sets</a>
        </div>
      </div>`;
    if (extrasEl) extrasEl.hidden = true;
    return;
  }
  if (extrasEl) extrasEl.hidden = false;

  container.innerHTML = cart.items.map(item => {
    const handle = item.handle;
    const rating = PRODUCT_RATINGS[handle];
    const isCaviar = CAVIAR_HANDLES.has(handle);
    const badgesHtml = [
      rating ? `<span class="mini-stars" aria-label="${rating.pr} stars">★ ${rating.pr} <em>(${rating.rc})</em></span>` : '',
      isCaviar ? `<span class="cites-badge" data-cites-tip="CITES certified — legally traceable aquaculture caviar with full documentation.">CITES certified<button class="cites-q" type="button" aria-label="What is CITES?">?</button></span>` : '',
    ].filter(Boolean).join('');

    const rawImg = (item.featured_image && item.featured_image.url)
      ? item.featured_image.url
      : (item.image || '');
    const imgSrc = rawImg ? rawImg.replace(/(\.[a-z]+)(\?|$)/, '_78x78$1$2') : '';

    const pdpUrl = `/products/${handle}`;
    return `<div class="mini-line" data-drawer-line="${item.key}">
      <a href="${pdpUrl}" tabindex="-1"><img src="${imgSrc}" alt="${item.product_title}" width="78" height="78" style="object-fit:cover;border-radius:4px"></a>
      <div class="mini-line-info">
        <a href="${pdpUrl}" style="font-weight:700;color:var(--ink);text-decoration:none;">${item.product_title}</a>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<span class="mini-variant">${item.variant_title}</span>` : ''}
        ${badgesHtml ? `<div class="mini-badges">${badgesHtml}</div>` : ''}
        <div class="mini-controls">
          <button type="button" data-drawer-change="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease">−</button>
          <span>${item.quantity}</span>
          <button type="button" data-drawer-change="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase">+</button>
        </div>
        <button type="button" class="mini-remove" data-drawer-change="${item.key}" data-qty="0" aria-label="Remove ${item.product_title}">Remove</button>
      </div>
      <b>${money(item.final_line_price)}</b>
    </div>`;
  }).join('');
}

// ── Cart drawer open / close ──────────────────────────────────────────────────

function openCartDrawer() {
  const drawer = $('#cart-drawer');
  const overlay = $('#cart-drawer-overlay');
  if (drawer) { drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); }
  if (overlay) overlay.classList.add('is-visible');
  document.body.style.overflow = 'hidden';
}

function closeCartDrawer() {
  const drawer = $('#cart-drawer');
  const overlay = $('#cart-drawer-overlay');
  if (drawer) { drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); }
  if (overlay) overlay.classList.remove('is-visible');
  document.body.style.overflow = '';
}

// ── Product card: add to cart ─────────────────────────────────────────────────

function initProductCards() {
  document.addEventListener('click', async e => {
    // + / − steps on cards
    const step = e.target.closest('[data-card-step]');
    if (step) {
      const chooser = step.closest('[data-product-chooser]');
      const qtyEl = chooser && chooser.querySelector('[data-card-qty]');
      if (qtyEl) {
        const next = Math.max(1, Number(qtyEl.textContent) + Number(step.dataset.cardStep));
        qtyEl.textContent = next;
      }
      return;
    }

    // Add to bag from card
    const addBtn = e.target.closest('[data-add-to-cart]');
    if (addBtn) {
      const chooser = addBtn.closest('[data-product-chooser]');
      const sizeSelect = chooser && chooser.querySelector('[data-card-size]');
      const qtyEl = chooser && chooser.querySelector('[data-card-qty]');
      const variantId = sizeSelect ? Number(sizeSelect.value) : Number(addBtn.dataset.variantId);
      const qty = qtyEl ? Math.max(1, Number(qtyEl.textContent)) : 1;

      if (!variantId) return;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      try {
        await cartAdd(variantId, qty);
        await refreshCartUI();
        toast('Added to bag');
        openCartDrawer();
      } catch {
        toast('Could not add item — please try again');
      } finally {
        addBtn.disabled = false;
        addBtn.textContent = 'Add to bag';
      }
      return;
    }

    // Cart drawer change (from drawer or mini-bag)
    const drawerChange = e.target.closest('[data-drawer-change]');
    if (drawerChange) {
      const key = drawerChange.dataset.drawerChange;
      const qty = Math.max(0, Number(drawerChange.dataset.qty));
      try {
        await cartChange(key, qty);
        await refreshCartUI();
      } catch {
        toast('Could not update bag');
      }
      return;
    }

    // Cart toggle (header bag icon)
    if (e.target.closest('[data-cart-toggle]')) {
      refreshCartUI().then(openCartDrawer);
      return;
    }

    // Close cart drawer
    if (e.target.closest('[data-cart-close]')) {
      closeCartDrawer();
      return;
    }
  });

  // Size select on cards updates displayed price
  document.addEventListener('change', e => {
    const sizeSelect = e.target.closest('[data-card-size]');
    if (!sizeSelect) return;
    const chosen = sizeSelect.options[sizeSelect.selectedIndex];
    const price = chosen.dataset.price;
    const chooser = sizeSelect.closest('[data-product-chooser]');
    const card = chooser && chooser.closest('.product-card');
    const priceEl = card && card.querySelector('[data-card-price]');
    if (priceEl && price) {
      priceEl.innerHTML = `${money(Number(price))}`;
    }
    const sizeLabel = card && card.querySelector('[data-card-size-label]');
    if (sizeLabel && chosen.dataset.title) {
      sizeLabel.textContent = chosen.dataset.title;
    }
  });
}

// ── Filter chips on homepage ──────────────────────────────────────────────────

function initFilterChips() {
  const row = $('[data-filter-row]');
  if (!row) return;

  row.addEventListener('click', e => {
    const chip = e.target.closest('[data-filter-chip]');
    if (!chip) return;

    $$('[data-filter-chip]').forEach(b => b.classList.remove('active'));
    chip.classList.add('active');

    const target = chip.dataset.filterChip;
    $$('[data-filter-grid]').forEach(grid => {
      grid.style.display = grid.dataset.filterGrid === target ? '' : 'none';
    });
  });
}

// ── PDP: variant selector, gallery, tabs ──────────────────────────────────────

function initPDP() {
  const form = $('[data-product-form]');
  if (!form) return;

  // Intercept add-to-cart
  form.addEventListener('submit', async e => {
    e.preventDefault();
    const variantId = Number($('#product-variant-id').value);
    const qty = Math.max(1, Number($('#product-qty').value || 1));
    const btn = $('#pdp-add-btn');
    if (!variantId || !btn) return;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      await cartAdd(variantId, qty);
      const giftBoxCheckbox = document.getElementById('gift-box-checkbox');
      if (giftBoxCheckbox && giftBoxCheckbox.checked) {
        await cartAdd(57782821552476, qty);
      }
      await refreshCartUI();
      toast('Added to bag');
      openCartDrawer();
    } catch {
      toast('Could not add item — please try again');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Add to bag';
    }
  });

  // Klarna instalment messaging
  function updateKlarna(pricePence) {
    const el = $('#klarna-msg');
    if (!el) return;
    el.hidden = pricePence < 3000;
    const amtEl = $('#klarna-amount');
    if (amtEl) amtEl.textContent = money(Math.floor(pricePence / 3));
  }

  // Variant size select → update price display and hidden input
  const sizeSelect = $('#product-size-select');
  function syncServingCards(variantId) {
    $$('.serving-card').forEach(c => c.classList.toggle('active', c.dataset.variantId === String(variantId)));
  }
  if (sizeSelect) {
    sizeSelect.addEventListener('change', () => {
      const opt = sizeSelect.options[sizeSelect.selectedIndex];
      const price = opt.dataset.price;
      const variantId = opt.value;
      const priceEl = $('#pdp-price');
      if (priceEl && price) priceEl.textContent = money(Number(price));
      updateKlarna(Number(price));
      const hiddenId = $('#product-variant-id');
      if (hiddenId) hiddenId.value = variantId;
      syncServingCards(variantId);
    });
    syncServingCards(sizeSelect.value);
    // Initialise Klarna from the currently selected option
    const initOpt = sizeSelect.options[sizeSelect.selectedIndex];
    if (initOpt?.dataset.price) updateKlarna(Number(initOpt.dataset.price));
  }

  // Serving card clicks → update size select
  document.addEventListener('click', e => {
    const card = e.target.closest('.serving-card');
    if (!card || !sizeSelect) return;
    const variantId = card.dataset.variantId;
    sizeSelect.value = variantId;
    sizeSelect.dispatchEvent(new Event('change'));
  });

  // Quantity +/− on PDP
  document.addEventListener('click', e => {
    const step = e.target.closest('[data-pdp-step]');
    if (!step) return;
    const qtyInput = $('#product-qty');
    if (!qtyInput) return;
    qtyInput.value = Math.max(1, Number(qtyInput.value) + Number(step.dataset.pdpStep));
  });

  // Gallery thumbnails
  document.addEventListener('click', e => {
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    $$('.pdp-thumb').forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
    const mainImg = $('[data-gallery-main]');
    if (mainImg) mainImg.src = thumb.dataset.galleryThumb;
  });

  // Image zoom lightbox
  const zoomModal = document.createElement('div');
  zoomModal.className = 'img-zoom-modal';
  zoomModal.innerHTML = '<button class="img-zoom-close" aria-label="Close">&times;</button><img alt="">';
  document.body.appendChild(zoomModal);
  const zoomImg = zoomModal.querySelector('img');

  function openZoom(src) {
    zoomImg.src = src;
    zoomModal.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function closeZoom() {
    zoomModal.classList.remove('open');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', e => {
    if (e.target.closest('[data-gallery-main]')) { openZoom(e.target.closest('[data-gallery-main]').src); return; }
    if (e.target === zoomModal || e.target.closest('.img-zoom-close')) closeZoom();
  });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeZoom(); });

  // PDP tabs
  document.addEventListener('click', e => {
    const tab = e.target.closest('[data-pdp-tab]');
    if (!tab) return;
    $$('.pdp-tab').forEach(t => { t.classList.remove('active'); t.setAttribute('aria-selected', 'false'); });
    tab.classList.add('active');
    tab.setAttribute('aria-selected', 'true');
    const name = tab.dataset.pdpTab;
    $$('[data-pdp-panel]').forEach(p => { p.hidden = p.dataset.pdpPanel !== name; });
  });
}

// ── Cart page: quantity controls ──────────────────────────────────────────────

function initCartPage() {
  const cartPage = $('#cart-page');
  if (!cartPage) return;

  cartPage.addEventListener('click', async e => {
    const qtyBtn = e.target.closest('.cart-qty-btn');
    if (qtyBtn) {
      const key = qtyBtn.dataset.key;
      const qty = Math.max(0, Number(qtyBtn.dataset.qty));
      try {
        await cartChange(key, qty);
        // Simple page reload to update Liquid-rendered totals
        window.location.reload();
      } catch {
        toast('Could not update bag');
      }
      return;
    }

    const removeBtn = e.target.closest('.cart-remove-btn');
    if (removeBtn) {
      const key = removeBtn.dataset.key;
      try {
        await cartChange(key, 0);
        window.location.reload();
      } catch {
        toast('Could not remove item');
      }
    }
  });
}

// ── PDP: "Read more" → activate Description tab and scroll ───────────────────

document.addEventListener('click', e => {
  const btn = e.target.closest('.pdp-read-more');
  if (!btn) return;
  const descTab = document.querySelector('[data-pdp-tab="description"]');
  if (descTab) {
    descTab.click();
    descTab.closest('.pdp-tabs-section').scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});

// Expose for Build Your Own page
window.refreshCartUI   = refreshCartUI;
window.openCartDrawer  = openCartDrawer;

// ── Init ──────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  initProductCards();
  initFilterChips();
  initPDP();
  initCartPage();

  // Auto-open membership modal when ?join=1 is in the URL
  if (new URLSearchParams(window.location.search).get('join') === '1') {
    const joinBtn = document.getElementById('cb-join-btn');
    if (joinBtn) joinBtn.click();
    // Clean the URL without reloading
    history.replaceState(null, '', window.location.pathname);
  }
});

// ── Gift box upgrade ──────────────────────────────────────────────────────────
(function () {
  var GIFT_BOX_VARIANT_ID = 57782821552476;
  var GIFT_BOX_PRICE_PENCE = 3000;

  var checkbox = document.getElementById('gift-box-checkbox');
  if (!checkbox) return;

  var form      = document.getElementById('product-form');
  var priceEl   = document.getElementById('pdp-price');
  if (!form || !priceEl) return;

  // Read the current set price from the element once
  var baseText  = priceEl.textContent.trim();
  var basePence = Math.round(parseFloat(baseText.replace(/[^0-9.]/g, '')) * 100);

  function formatMoney(pence) {
    return '£' + (pence / 100).toFixed(2);
  }

  // Hidden input that carries the gift box into the cart submission
  var hiddenInput = document.createElement('input');
  hiddenInput.type  = 'hidden';
  hiddenInput.name  = 'items[][id]';
  hiddenInput.value = GIFT_BOX_VARIANT_ID;
  hiddenInput.disabled = true;
  form.appendChild(hiddenInput);

  checkbox.addEventListener('change', function () {
    var checked = checkbox.checked;
    hiddenInput.disabled = !checked;
    document.getElementById('gift-box-upgrade').classList.toggle('gift-box-upgrade--selected', checked);
    priceEl.textContent = checked
      ? formatMoney(basePence + GIFT_BOX_PRICE_PENCE)
      : formatMoney(basePence);
  });
})();

// ── Article: sync "Shop the Recipe" prices with live product prices ───────────
(function () {
  var body = document.querySelector('.article-body');
  if (!body) return;

  var links = Array.from(body.querySelectorAll('a[href*="/products/"]'));
  if (!links.length) return;

  // Group links by product handle, preserve variant hint from URL
  var handles = {};
  links.forEach(function (a) {
    var m = a.href.match(/\/products\/([^/?#]+)/);
    if (!m) return;
    var handle = m[1];
    var variantMatch = a.href.match(/[?&]variant=(\d+)/);
    if (!handles[handle]) handles[handle] = { links: [], variantId: variantMatch ? variantMatch[1] : null };
    handles[handle].links.push(a);
    if (variantMatch) handles[handle].variantId = variantMatch[1];
  });

  Object.keys(handles).forEach(function (handle) {
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        var entry = handles[handle];

        // Find price: prefer the specific variant if URL had one
        var pricePence = null;
        if (entry.variantId && data.variants) {
          var v = data.variants.find(function (v) { return String(v.id) === entry.variantId; });
          if (v) pricePence = v.price;
        }
        if (pricePence == null) pricePence = data.price_min;

        // Replace £XX or £XX.XX in each link's text
        entry.links.forEach(function (a) {
          a.textContent = a.textContent.replace(/£[\d,]+(?:\.\d{2})?/, money(pricePence));
        });
      })
      .catch(function () {});
  });
})();

// ── Cart: gift message toggle + autosave ─────────────────────────────────────
(function () {
  var toggle = document.getElementById('gift-msg-toggle');
  var field  = document.getElementById('gift-msg-field');
  var note   = document.getElementById('cart-gift-note');
  var saved  = document.getElementById('gift-msg-saved');
  if (!toggle || !field) return;

  toggle.addEventListener('click', function () {
    var open = field.hidden;
    field.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = open ? '− Hide gift message' : '+ Add a gift message';
    if (open && note) note.focus();
  });

  if (note) {
    var saveTimer;
    note.addEventListener('input', function () {
      clearTimeout(saveTimer);
      if (saved) saved.textContent = '';
      saveTimer = setTimeout(function () {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.value }),
        }).then(function () {
          if (saved) { saved.textContent = 'Saved'; setTimeout(function () { saved.textContent = ''; }, 1800); }
        });
      }, 800);
    });
  }
})();

// ── Cart: upsell "+" buttons ──────────────────────────────────────────────────
document.addEventListener('click', async function (e) {
  var btn = e.target.closest('[data-upsell-variant]');
  if (!btn) return;
  var variantId = Number(btn.dataset.upsellVariant);
  if (!variantId) return;
  btn.disabled = true;
  btn.textContent = '…';
  try {
    await cartAdd(variantId, 1);
    await refreshCartUI();
    toast('Added to bag');
  } catch {
    toast('Could not add item — please try again');
  } finally {
    btn.disabled = false;
    btn.textContent = '+';
  }
});

// ── CITES tooltip — appended to body so it escapes transformed cart drawer ────
(function () {
  // Single global tooltip node outside any transformed ancestor
  var tip = document.createElement('div');
  tip.className = 'cites-tooltip';
  document.body.appendChild(tip);

  function show(badge) {
    var text = badge.dataset.citesTip;
    if (!text) return;
    tip.textContent = text;
    var r = badge.getBoundingClientRect();
    tip.style.top  = (r.bottom + 8) + 'px';
    tip.style.left = Math.min(r.left, window.innerWidth - 222) + 'px';
    tip.classList.add('is-visible');
  }
  function hide() { tip.classList.remove('is-visible'); }

  // Desktop: hover badge
  document.addEventListener('mouseover', function (e) {
    var b = e.target.closest('.cites-badge');
    if (b) show(b);
  });
  document.addEventListener('mouseout', function (e) {
    var b = e.target.closest('.cites-badge');
    if (b && !b.contains(e.relatedTarget)) hide();
  });

  // Mobile: tap ? button
  document.addEventListener('click', function (e) {
    var q = e.target.closest('.cites-q');
    if (q) {
      var b = q.closest('.cites-badge');
      if (tip.classList.contains('is-visible')) { hide(); } else { show(b); }
      return;
    }
    if (!e.target.closest('.cites-badge')) hide();
  });

  document.addEventListener('scroll', hide, true);
})();

// ── Safety: reset body overflow on page leave + Escape key ───────────────────
window.addEventListener('pagehide', function () {
  document.body.style.overflow = '';
});

document.addEventListener('keydown', function (e) {
  if (e.key !== 'Escape') return;
  // Close cart drawer
  var drawer = document.getElementById('cart-drawer');
  if (drawer && drawer.classList.contains('is-open')) {
    closeCartDrawer();
    return;
  }
  // Close mobile nav
  var nav = document.getElementById('mobile-nav');
  var btn = document.getElementById('nav-hamburger');
  if (nav && nav.classList.contains('open')) {
    nav.classList.remove('open');
    nav.setAttribute('aria-hidden', 'true');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }
});

// ── Mobile nav hamburger ──────────────────────────────────────────────────────
(function () {
  var btn = document.getElementById('nav-hamburger');
  var nav = document.getElementById('mobile-nav');
  if (!btn || !nav) return;

  btn.addEventListener('click', function () {
    var open = nav.classList.toggle('open');
    btn.setAttribute('aria-expanded', String(open));
    nav.setAttribute('aria-hidden', String(!open));
  });

  // Close on outside click
  document.addEventListener('click', function (e) {
    if (!nav.classList.contains('open')) return;
    if (!nav.contains(e.target) && !btn.contains(e.target)) {
      nav.classList.remove('open');
      btn.setAttribute('aria-expanded', 'false');
      nav.setAttribute('aria-hidden', 'true');
    }
  });

  // Close on nav link click
  nav.querySelectorAll('a').forEach(function (a) {
    a.addEventListener('click', function () {
      nav.classList.remove('open');
      btn.setAttribute('aria-expanded', 'false');
      nav.setAttribute('aria-hidden', 'true');
    });
  });
})();

// ── Auth icon dropdown ────────────────────────────────────────────────────────
(function () {
  var wrap = document.querySelector('.auth-icon-wrap');
  if (!wrap) return;
  var btn = wrap.querySelector('.auth-icon-btn');
  var dropdown = wrap.querySelector('.auth-dropdown');

  btn.addEventListener('click', function (e) {
    e.stopPropagation();
    var open = dropdown.classList.toggle('open');
    btn.setAttribute('aria-expanded', open);
    dropdown.setAttribute('aria-hidden', !open);
  });

  document.addEventListener('click', function (e) {
    if (!dropdown.classList.contains('open')) return;
    if (!wrap.contains(e.target)) {
      dropdown.classList.remove('open');
      btn.setAttribute('aria-expanded', 'false');
      dropdown.setAttribute('aria-hidden', 'true');
    }
  });
})();

// ── Desktop dropdown nav ──────────────────────────────────────────────────────
(function () {
  var wraps = document.querySelectorAll('.nav-dd-wrap');
  if (!wraps.length) return;

  function closeAll(except) {
    wraps.forEach(function (w) {
      if (w === except) return;
      w.classList.remove('open');
      var btn = w.querySelector('.nav-dd-trigger');
      var panel = w.querySelector('.nav-dd-panel');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (panel) panel.setAttribute('aria-hidden', 'true');
    });
  }

  wraps.forEach(function (wrap) {
    var btn   = wrap.querySelector('.nav-dd-trigger');
    var panel = wrap.querySelector('.nav-dd-panel');
    if (!btn || !panel) return;

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      var isOpen = wrap.classList.toggle('open');
      btn.setAttribute('aria-expanded', isOpen);
      panel.setAttribute('aria-hidden', !isOpen);
      if (isOpen) closeAll(wrap);
    });

    // Desktop hover open/close
    wrap.addEventListener('mouseenter', function () {
      if (window.innerWidth < 1024) return;
      closeAll(wrap);
      wrap.classList.add('open');
      btn.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
    });
    wrap.addEventListener('mouseleave', function () {
      if (window.innerWidth < 1024) return;
      wrap.classList.remove('open');
      btn.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
    });

    // Close on Escape
    wrap.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeAll();
        btn.focus();
      }
    });
  });

  document.addEventListener('click', function () { closeAll(); });
})();

// ── Mobile accordion nav ──────────────────────────────────────────────────────
(function () {
  var btns = document.querySelectorAll('[data-mobile-toggle]');
  btns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var targetId = btn.getAttribute('data-mobile-toggle');
      var sub = document.getElementById(targetId);
      if (!sub) return;
      var isOpen = !sub.hidden;
      sub.hidden = isOpen;
      btn.setAttribute('aria-expanded', !isOpen);
    });
  });
})();

// ── Search overlay ────────────────────────────────────────────────────────────
(function () {
  var toggle  = document.getElementById('search-toggle');
  var overlay = document.getElementById('search-overlay');
  var input   = document.getElementById('search-input');
  var close   = document.getElementById('search-close');
  if (!toggle || !overlay) return;

  function openSearch() {
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
    if (input) input.focus();
  }

  function closeSearch() {
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-expanded', 'false');
  }

  toggle.addEventListener('click', function () {
    overlay.classList.contains('is-open') ? closeSearch() : openSearch();
  });

  if (close) close.addEventListener('click', closeSearch);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeSearch();
  });

  document.addEventListener('click', function (e) {
    if (!overlay.classList.contains('is-open')) return;
    if (!overlay.contains(e.target) && !toggle.contains(e.target)) closeSearch();
  });
})();

